import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { CheckCircle2, Loader2, XCircle, Video, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setInfo(res.data); setState("paid"); return; }
        if (["expired", "failed"].includes(res.data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center px-4 relative z-10">
      <div className="w-full max-w-md bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-8 text-center">
        <div className="flex justify-center mb-6"><Logo size={48} /></div>
        {state === "checking" && (<>
          <Loader2 className="w-12 h-12 text-[#FF6B6B] animate-spin mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl uppercase tracking-tight text-white">Confirming payment…</h1>
          <p className="text-sm text-[#A0A0A0] mt-2">Hang tight while we verify with Stripe.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 text-[#FF6B6B] mx-auto mb-4" data-testid="payment-success-icon" />
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight text-white">Payment complete</h1>
          <p className="text-sm text-[#A0A0A0] mt-2 mb-6">
            {info?.item_type === "subscription" ? "Your membership is active." :
             info?.item_type === "session" ? "Your session is booked — a calendar invite is on its way." :
             "Your purchase is in your library."}
          </p>
          {info?.result?.booking_id ? (
            <button data-testid="go-live-btn" onClick={() => navigate(`/live/${info.result.booking_id}`)} className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all mb-2">
              <Video className="w-4 h-4" /> Go to Live Room
            </button>
          ) : null}
          <button data-testid="back-to-app-btn" onClick={() => navigate(info?.item_type === "session" ? "/dashboard" : "/dashboard")} className="w-full py-3 rounded-xl bg-[#2A2A2A] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#333333] transition-colors">
            Back to My Hub <ArrowRight className="w-4 h-4" />
          </button>
        </>)}
        {(state === "timeout" || state === "error") && (<>
          <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl uppercase tracking-tight text-white">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-sm text-[#A0A0A0] mt-2 mb-6">{state === "timeout" ? "Payment is taking longer than usual. Check My Hub shortly." : "We couldn't confirm this payment."}</p>
          <button data-testid="back-to-app-btn" onClick={() => navigate("/dashboard")} className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm hover:brightness-110 transition-all">Back to My Hub</button>
        </>)}
      </div>
    </div>
  );
}
